// api
import getTicketsAPICall from '../../api/getTickets';
// logic
import moment from 'moment';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
// settings
import { SETTINGS } from 'modules/emergencies/constants';
// sound
import { Howl } from 'howler';

const TAB_NOTIFICATION_POLLING_INTERVAL =
  SETTINGS.NEW_TICKET_NOTIFICATIONS_POLLING_INTERVAL_IN_SECONDS * 1000;

const PAGE_SIZE = SETTINGS.NEW_TICKET_NOTIFICATIONS_PAGE_SIZE;

const createdAfterDate = moment().subtract(7, 'days').format('YYYY-MM-DDTHH:mm:ss');

function useNewTicketNotifications(enableBroadcast = true) {
  const location = useLocation();
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  const [previousIds, setPreviousIds] = useState([]);
  const [newDataAvailable, setNewDataAvailable] = useState(false);
  const [isVisible, setIsVisible] = useState(!document.hidden);

  // files are located in /public folder
  const soundPath = '/new-ticket-notifications-resources/sound-new-ticket-notification.mp3';
  const newFaviconPath = '/new-ticket-notifications-resources/icon-new-ticket-notification.png';
  const newTitle = 'XPO New Ticket';

  const sound = useMemo(
    () =>
      new Howl({
        src: soundPath,
      }),
    []
  );

  const [originalFaviconPath] = useState(
    document.querySelector("link[rel='icon']").getAttribute('href')
  );
  const [originalTitle] = useState(document.title);

  const setNotificationUI = useCallback(() => {
    document.title = newTitle;
    document.querySelector("link[rel='icon']").setAttribute('href', newFaviconPath);
  }, []);

  const restoreDefaultUI = useCallback(() => {
    document.title = originalTitle;
    document.querySelector("link[rel='icon']").setAttribute('href', originalFaviconPath);
  }, [originalFaviconPath, originalTitle]);

  const playNotificationSound = useCallback(() => {
    sound.play();
  }, [sound]);

  const triggerNotification = useCallback(() => {
    setNotificationUI();
    playNotificationSound();
  }, [setNotificationUI, playNotificationSound]);

  const handleTabVisibilityChange = () => {
    setIsVisible(!document.hidden);
  };

  let bc;
  if (enableBroadcast && typeof window !== 'undefined') {
    bc = new BroadcastChannel('ticket-notifications');
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpointURL = `tickets/?page_size=${PAGE_SIZE}&page=1&created_by_shipper=true&created_at_after=${createdAfterDate}`;
        const response = await getTicketsAPICall(endpointURL);
        const newIds = response.map((item) => item.id);
        const shouldUpdateNewDataAvailable =
          !isFirstFetch &&
          (newIds.length !== previousIds.length || newIds.some((id) => !previousIds.includes(id)));

        if (shouldUpdateNewDataAvailable) {
          setNewDataAvailable(true);
          if (enableBroadcast) {
            bc.postMessage('newData');
          }
        } else {
          setNewDataAvailable(false);
        }

        setPreviousIds(newIds);
        setIsFirstFetch(false);
      } catch (err) {
        console.error(err);
      }
    };
    const intervalId = setInterval(fetchData, TAB_NOTIFICATION_POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [isFirstFetch, previousIds, enableBroadcast, bc]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleTabVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleTabVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (newDataAvailable && !isVisible) {
      triggerNotification();
    }
  }, [newDataAvailable, isVisible, triggerNotification]);

  useEffect(() => {
    if (isVisible && location.pathname === '/emergencies') {
      restoreDefaultUI();
      setNewDataAvailable(false);
      if (enableBroadcast) {
        bc.postMessage('resetData');
      }
    }
  }, [isVisible, location.pathname, enableBroadcast, bc, restoreDefaultUI]);

  useEffect(() => {
    if (enableBroadcast) {
      const handleBroadcast = (message) => {
        if (message.data === 'newData' && !isVisible) {
          triggerNotification();
        } else if (
          message.data === 'resetData' &&
          location.pathname === '/emergencies' &&
          isVisible
        ) {
          restoreDefaultUI();
          setNewDataAvailable(false);
        }
      };

      bc.addEventListener('message', handleBroadcast);
      return () => {
        bc.removeEventListener('message', handleBroadcast);
      };
    }
  }, [enableBroadcast, isVisible, location.pathname, bc, triggerNotification, restoreDefaultUI]);

  return {};
}

export default useNewTicketNotifications;
